import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// react-leaflet v5 doesn't ship marker icons with a bundler-friendly path.
// Patch the default so markers render without extra work.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: () => string })._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const riderIcon = L.divIcon({
    html: `<div style="background:#10b981;color:#fff;border-radius:50%;width:34px;height:34px;display:grid;place-items:center;box-shadow:0 4px 14px rgba(16,185,129,.4);border:3px solid #fff;font-size:16px">🛵</div>`,
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

const customerIcon = L.divIcon({
    html: `<div style="background:#0f172a;color:#fff;border-radius:50%;width:30px;height:30px;display:grid;place-items:center;box-shadow:0 4px 14px rgba(15,23,42,.35);border:3px solid #fff;font-size:14px">🏠</div>`,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});

export type LatLng = { lat: number; lng: number };

type Props = {
    rider?: LatLng | null;
    customer?: LatLng | null;
    path?: LatLng[];
    height?: number | string;
    zoom?: number;
    testId?: string;
    markers?: Array<{ position: LatLng; label: string; icon?: L.DivIcon }>;
};

/** Auto-recentres/fits when either endpoint moves. */
function FitBounds({ points }: { points: LatLng[] }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        if (points.length === 1) {
            map.setView([points[0].lat, points[0].lng], 15, { animate: true });
            return;
        }
        const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
        map.fitBounds(bounds.pad(0.2), { animate: true });
    }, [points, map]);
    return null;
}

export function LiveMap({
    rider,
    customer,
    path,
    height = 260,
    zoom = 14,
    testId,
    markers,
}: Props) {
    // Fallback centre if we have nothing yet — Bangalore centre.
    const centre: LatLng = rider || customer || { lat: 12.9716, lng: 77.5946 };
    const fitPoints: LatLng[] = [
        ...(rider ? [rider] : []),
        ...(customer ? [customer] : []),
        ...(markers?.map((m) => m.position) || []),
    ];
    return (
        <div
            data-testid={testId}
            className="overflow-hidden rounded-2xl border border-border/70 shadow-soft"
            style={{ height }}
        >
            <MapContainer
                center={[centre.lat, centre.lng]}
                zoom={zoom}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
                attributionControl={false}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {fitPoints.length > 0 ? <FitBounds points={fitPoints} /> : null}
                {rider ? (
                    <Marker position={[rider.lat, rider.lng]} icon={riderIcon}>
                        <Popup>Your rider is here</Popup>
                    </Marker>
                ) : null}
                {customer ? (
                    <Marker position={[customer.lat, customer.lng]} icon={customerIcon}>
                        <Popup>Drop-off</Popup>
                    </Marker>
                ) : null}
                {markers?.map((m, i) => (
                    <Marker
                        key={`${m.position.lat}-${m.position.lng}-${i}`}
                        position={[m.position.lat, m.position.lng]}
                        icon={m.icon || riderIcon}
                    >
                        <Popup>{m.label}</Popup>
                    </Marker>
                ))}
                {path && path.length > 1 ? (
                    <Polyline
                        positions={path.map((p) => [p.lat, p.lng] as [number, number])}
                        pathOptions={{ color: "#10b981", weight: 4, opacity: 0.7 }}
                    />
                ) : null}
            </MapContainer>
        </div>
    );
}

export { riderIcon, customerIcon };
export default LiveMap;
